import { normalizeCountryCodeValue } from '~/stores/app'

export type HomeHolidaySkin = 'mid-autumn' | 'national-day' | null

const MID_AUTUMN_COUNTRIES = new Set(['156', '344', '458', '158', '764', '704', 'cn', 'hk', 'my', 'tw', 'th', 'vn'])
const NATIONAL_DAY_COUNTRIES = new Set(['156', '344', 'cn', 'hk'])

const parseDateString = (value: string) => {
  const normalized = value
    .trim()
    .replace(/年|月/g, '-')
    .replace(/日/g, '')
    .replace(/[./]/g, '-')
    .replace(/\s+/, 'T')
  const match = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:T(\d{1,2})(?::(\d{1,2}))?(?::(\d{1,2}))?)?$/)

  if (!match) return null

  const [, year, month, day, hour = '0', minute = '0', second = '0'] = match
  const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second))
  return Number.isNaN(date.getTime()) ? null : date.getTime()
}

/** 将测试时间统一转换为毫秒；数字 10 位以内按秒时间戳处理。 */
export const parseHomeHolidayTime = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e11 ? value * 1000 : value
  }

  if (typeof value !== 'string' || !value.trim()) return null

  const rawValue = value.trim()
  if (/^\d+$/.test(rawValue)) {
    const numericValue = Number(rawValue)
    return Number.isFinite(numericValue) ? (rawValue.length <= 10 ? numericValue * 1000 : numericValue) : null
  }

  const dateValue = parseDateString(rawValue)
  if (dateValue !== null) return dateValue

  const parsed = Date.parse(rawValue)
  return Number.isNaN(parsed) ? null : parsed
}

const isBetween = (value: number, startMonth: number, startDay: number, endMonth: number, endDay: number) => {
  const year = new Date(value).getFullYear()
  const start = new Date(year, startMonth - 1, startDay).getTime()
  const end = new Date(year, endMonth - 1, endDay).getTime()
  return value >= start && value < end
}

/** SSR 与客户端共用节日判定，避免首屏皮肤和 hydration 结果不一致。 */
export const resolveHomeHolidaySkin = (options: {
  countryCode?: unknown
  now: number
  allowQueryOverride: boolean
  queryTime?: unknown
}): HomeHolidaySkin => {
  const countryCode = normalizeCountryCodeValue(options.countryCode).toLowerCase()
  if (!countryCode) return null

  const effectiveNow = options.allowQueryOverride
    ? (parseHomeHolidayTime(options.queryTime) ?? options.now)
    : options.now

  if (NATIONAL_DAY_COUNTRIES.has(countryCode) && isBetween(effectiveNow, 10, 1, 10, 8)) {
    return 'national-day'
  }

  if (MID_AUTUMN_COUNTRIES.has(countryCode) && isBetween(effectiveNow, 9, 25, 9, 28)) {
    return 'mid-autumn'
  }

  return null
}

export const useHomeHolidaySkin = () => {
  const route = useRoute()
  const appStore = useAppStore()
  const runtimeConfig = useRuntimeConfig()
  const serverNow = useState<number>('home-server-now', () => Date.now())
  const allowQueryOverride = runtimeConfig.public.appEnv !== 'production'

  const holidaySkin = computed(() =>
    resolveHomeHolidaySkin({
      countryCode: appStore.CountryCode || appStore.myCountry,
      now: serverNow.value,
      allowQueryOverride,
      queryTime: route.query.t,
    }),
  )

  return {
    allowQueryOverride,
    holidaySkin,
    serverNow,
  }
}
